const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface Resource {
  id: number;
  title: string;
  description: string;
  category: string;
  priority: number;
  is_active: boolean;
  require_email: boolean;
  file_count: number;
  download_count: number;
  metadata: any;
  assigned_segments: Array<{
    id: number;
    name: string;
    is_primary: boolean;
  }>;
  assigned_quiz_profiles?: Array<{
    id: number;
    name: string;
    slug: string;
    is_primary: boolean;
  }>;
  files: Array<{
    id: number;
    filename: string;
    file_type: string;
  }>;
  created_at: string;
  updated_at?: string;
}

export interface CreateResourceData {
  title: string;
  description: string;
  category: string;
  priority: number;
  require_email: boolean;
  segment_ids: number[];
  quiz_profile_ids: number[];
  metadata: any;
  files: File[];
}

export interface ResourceStats {
  period_days: number;
  email_captures: number;
  downloads: number;
  conversion_rate: number;
}

export interface DownloadToken {
  success: boolean;
  token: string;
  expires_at: string;
  download_url: string;
  resource: {
    id: number;
    title: string;
    description: string;
  };
}

class ResourceService {
  private getAuthHeaders() {
    const token = localStorage.getItem('chatbot_admin_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  private getAuthHeadersForFormData() {
    const token = localStorage.getItem('chatbot_admin_token');
    return {
      'Authorization': `Bearer ${token}`,
      // Don't set Content-Type for FormData - browser will set it with boundary
    };
  }

  async getResources(category?: string, segment?: string): Promise<Resource[]> {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (segment) params.append('segment', segment);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const response = await fetch(`${API_BASE_URL}/api/resources${queryString}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch resources: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching resources:', error);
      throw error;
    }
  }

  async createResource(data: CreateResourceData): Promise<{ success: boolean; resource_id: number; message: string }> {
    try {
      const formData = new FormData();
      
      // Append text fields
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('category', data.category);
      formData.append('priority', data.priority.toString());
      formData.append('require_email', data.require_email.toString());
      formData.append('metadata', JSON.stringify(data.metadata));

      // Append segment IDs
      data.segment_ids.forEach(id => {
        formData.append('segment_ids', id.toString());
      });

      // Append files
      data.files.forEach(file => {
        formData.append('files', file);
      });

      console.log('📤 Uploading resource with FormData:', {
        title: data.title,
        fileCount: data.files.length,
        segmentCount: data.segment_ids.length
      });

      const response = await fetch(`${API_BASE_URL}/api/resources`, {
        method: 'POST',
        headers: this.getAuthHeadersForFormData(),
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to create resource: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating resource:', error);
      throw error;
    }
  }

  async updateResource(id: number, updates: Partial<Resource>): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error(`Failed to update resource: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating resource:', error);
      throw error;
    }
  }

  async deleteResource(id: number): Promise<{ success: boolean }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to delete resource: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting resource:', error);
      throw error;
    }
  }

  async assignToSegments(resourceId: number, segmentIds: number[], clearExisting: boolean = false): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/${resourceId}/assign-segments`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          segment_ids: segmentIds,
          clear_existing: clearExisting
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to assign segments: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error assigning segments:', error);
      throw error;
    }
  }

  async assignToQuizProfiles(resourceId: number, profileIds: number[], clearExisting: boolean = false): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/${resourceId}/assign-quiz-profiles`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          profile_ids: profileIds,
          clear_existing: clearExisting
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to assign quiz profiles: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error assigning quiz profiles:', error);
      throw error;
    }
  }

  async getResourceStats(id: number, days: number = 30): Promise<ResourceStats> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/${id}/stats?days=${days}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch resource stats: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching resource stats:', error);
      throw error;
    }
  }

  async getResourceFiles(id: number): Promise<Array<{ id: number; original_name: string; file_type: string; file_size: number; is_primary: boolean; description?: string }>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/${id}/files`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch resource files: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching resource files:', error);
      throw error;
    }
  }

  async generateDownloadToken(resourceId: number, email: string, visitorUuid?: string): Promise<DownloadToken> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/generate-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resource_id: resourceId,
          email,
          visitor_uuid: visitorUuid
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to generate download token: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error generating download token:', error);
      throw error;
    }
  }

  async getResourcesBySegment(segmentName: string, limit: number = 3): Promise<{ segment: string; resources: Resource[] }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resources/by-segment/${segmentName}?limit=${limit}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch resources for segment: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching resources by segment:', error);
      throw error;
    }
  }

  // Utility methods
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileTypeIcon(fileType: string): string {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return '📄';
      case 'docx':
      case 'doc':
        return '📝';
      case 'mp4':
      case 'webm':
        return '🎥';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return '🖼️';
      default:
        return '📎';
    }
  }

  getCategoryColor(category: string): string {
    const colors: { [key: string]: string } = {
      'autism': 'bg-blue-100 text-blue-800',
      'adhd': 'bg-green-100 text-green-800',
      'learning-differences': 'bg-purple-100 text-purple-800',
      'speech-language': 'bg-orange-100 text-orange-800',
      'behavioral-support': 'bg-red-100 text-red-800',
      'general': 'bg-gray-100 text-gray-800'
    };
    
    return colors[category] || colors['general'];
  }
}

const resourceService = new ResourceService();
export default resourceService;